import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';
import miaFlowAuth from '../auth/MiaFlowAuth';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import Icon from '../components/icons/Icon';
import './PlaybookLauncher.css';

const AI_API_PREFIX = 'api/catalog'

/**
 * PlaybookLauncher
 *
 * Opened when the user clicks a playbook in the sidebar (/playbook/:playbookId).
 * It loads the playbook, shows its composition summary, and lets the user
 * open a pre-configured Chat or Code session with all the playbook's
 * agents, tools, prompts and specs already applied.
 */
const PlaybookLauncher = () => {
  const { playbookId } = useParams();
  const navigate = useNavigate();

  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
    if (!tenantId || !playbookId) {
      setError('No tenant or playbook ID found.');
      setLoading(false);
      return;
    }

    const baseUrl = config.bffUrl || window.location.origin;
    const params = new URLSearchParams({ tenantId, page: '1', perPage: '100' });

    // Fetch all playbooks and find the one matching playbookId
    getAuthorizedFetch()(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${params}`, { headers: getBffHeaders() })
      .then(r => r.json())
      .then(result => {
        if (result.success && result.data) {
          const found = (result.data.items || []).find(i => i.itemId === playbookId);
          if (found) {
            setPlaybook(found);
          } else {
            setError('Playbook not found.');
          }
        } else {
          setError(result.error || 'Failed to load playbook.');
        }
      })
      .catch(err => setError(err.message || 'Network error'))
      .finally(() => setLoading(false));
  }, [playbookId]);

  const launch = (mode) => {
    if (!playbook) return;

    // Persist playbook context so MainChat can pick it up on mount
    try {
      sessionStorage.setItem('mia_active_playbook', JSON.stringify(playbook));
    } catch (_) { /* ignore */ }

    const tenant = miaFlowAuth.getSelectedTenant();
    const project = miaFlowAuth.getSelectedProject();
    const tenantId = tenant?.tenantId;

    if (mode === 'code' && tenantId) {
      const projectId = project?.projectId;
      if (projectId) {
        navigate(`/chat/${tenantId}/${projectId}?playbookId=${playbookId}`);
      } else {
        navigate(`/chat/${tenantId}?playbookId=${playbookId}`);
      }
    } else {
      navigate(`/compose?playbookId=${playbookId}`);
    }
  };

  const effectiveMode = playbook?.mode || 'chat';

  // Auto-launch when mode is not 'both'
  useEffect(() => {
    if (!playbook) return;
    if (effectiveMode !== 'both') {
      launch(effectiveMode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbook]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="playbook-launcher-page">
        <div className="playbook-launcher-loading">
          <Icon name="zap" size={36} />
          <p>Loading playbook…</p>
        </div>
      </div>
    );
  }

  if (error || !playbook) {
    return (
      <div className="playbook-launcher-page">
        <div className="playbook-launcher-error">
          <Icon name="alert-circle" size={36} />
          <p>{error || 'Playbook not found.'}</p>
          <button className="il-btn-secondary" onClick={() => navigate('/catalog/playbooks')}>
            Back to Playbook catalog
          </button>
        </div>
      </div>
    );
  }

  // v1 compat: agents come from .agents (old) or .flow.nodes type="agent" (new)
  const resolvedAgentRefs = (() => {
    if (Array.isArray(playbook.agents) && playbook.agents.length > 0) return playbook.agents;
    return ((playbook.flow?.nodes) || [])
        .filter(n => n.type === 'agent' && n.ref)
        .map(n => ({ itemId: n.ref, name: n.ref }));
  })();

  const COMPONENT_DEFS = [
    { key: '_agents', label: 'AI Agents',    icon: 'users',    items: resolvedAgentRefs },
    { key: 'flows',   label: 'Agentic Flows', icon: 'sequence', items: playbook.flows || [] },
    { key: 'prompts', label: 'AI Prompts',    icon: 'message',  items: playbook.prompts || [] },
    { key: 'specs',   label: 'AI Specs',      icon: 'code',     items: playbook.specs || [] },
    { key: 'skills',  label: 'Agent Skills',  icon: 'zap',      items: playbook.skills || [] },
    { key: 'tools',   label: 'Tools',         icon: 'settings', items: playbook.tools || [] },
  ];

  const hasComponents = COMPONENT_DEFS.some(d => d.items.length > 0);

  return (
    <div className="playbook-launcher-page">
      <div className="playbook-launcher-card">
        {/* Header ── */}
        <div className="il-header">
          <div className="il-header-icon">
            <Icon name="zap" size={28} />
          </div>
          <div className="il-header-content">
            <div className="il-eyebrow">Playbook</div>
            <h1 className="il-title">{playbook.name}</h1>
            {playbook.description && <p className="il-desc">{playbook.description}</p>}
          </div>
          <div className={`il-mode-badge ${effectiveMode}`}>
            <Icon name={effectiveMode === 'both' ? 'zap' : effectiveMode === 'code' ? 'code' : 'message'} size={13} />
            {effectiveMode === 'both' ? 'Chat + Code' : effectiveMode === 'code' ? 'Code' : 'Chat'} mode
          </div>
        </div>

        {/* Components ── */}
        {hasComponents && (
          <div className="il-components">
            <div className="il-components-label">Preconfigured components</div>
            <div className="il-components-grid">
              {COMPONENT_DEFS.map(d => {
                const items = d.items;
                if (items.length === 0) return null;
                return (
                  <div key={d.key} className="il-component-group">
                    <div className="il-component-group-header">
                      <Icon name={d.icon} size={13} />
                      <span>{d.label}</span>
                      <span className="il-count">{items.length}</span>
                    </div>
                    <div className="il-chips">
                      {items.map((item, i) => (
                        <span key={i} className="il-chip">{item.name || item.itemId || item}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions ── */}
        <div className="il-actions">
          {effectiveMode === 'code' ? (
            <>
              <button className="il-btn-primary" onClick={() => launch('code')}>
                <Icon name="code" size={16} /> Open in Code
              </button>
              <button className="il-btn-secondary" onClick={() => launch('chat')}>
                <Icon name="message" size={16} /> Open in Chat
              </button>
            </>
          ) : effectiveMode === 'both' ? (
            <>
              <button className="il-btn-primary" onClick={() => launch('chat')}>
                <Icon name="message" size={16} /> Open in Chat
              </button>
              <button className="il-btn-primary" onClick={() => launch('code')}>
                <Icon name="code" size={16} /> Open in Code
              </button>
            </>
          ) : (
            <>
              <button className="il-btn-primary" onClick={() => launch('chat')}>
                <Icon name="message" size={16} /> Open in Chat
              </button>
              <button className="il-btn-secondary" onClick={() => launch('code')}>
                <Icon name="code" size={16} /> Open in Code
              </button>
            </>
          )}
          <button className="il-btn-ghost" onClick={() => navigate(`/catalog/playbooks/${playbookId}`)}>
            <Icon name="package" size={14} /> Manage in Playbook catalog
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaybookLauncher;
